import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { StableInput, StableTextarea } from '../components/StableInput';
import { Label } from '../components/ui/label';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { WatchlistTags } from '../components/WatchlistTags';
import { googleCalendarUrl, downloadIcs, parseDeadline } from '../lib/calendarExport';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  FileText,
  Calendar,
  DollarSign,
  Trash2,
  Edit2,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  PenLine,
  Send,
  Star,
  Bookmark,
  BookmarkCheck,
  Target,
  Sparkles,
  ExternalLink,
  Download,
  Filter,
  TrendingUp,
  Clock,
  Eye,
  FileCheck,
  RefreshCw,
  Lock,
  Edit,
  ChevronDown,
  ChevronUp,
  MapPin,
  Building,
  X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  planning: { label: 'Planning', icon: PenLine, color: 'bg-stone-100 text-stone-700' },
  drafting: { label: 'Drafting', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-700' },
  submitted: { label: 'Submitted', icon: Send, color: 'bg-blue-100 text-blue-700' },
  awarded: { label: 'Awarded', icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-700' },
};

const TEMPLATE_TYPE_LABELS = {
  loi: 'Letter of Intent',
  proposal: 'Full Proposal',
  budget: 'Budget Narrative',
  narrative: 'Program Narrative',
  other: 'Other',
};

const FOCUS_AREAS = [
  'Education', 'Health', 'Environment', 'Arts & Culture', 'Human Services',
  'Community Development', 'Youth Development', 'Housing', 'Food Security',
  'Mental Health', 'Workforce Development', 'Animal Welfare', 'Disaster Relief',
  'STEM', 'Criminal Justice', 'Civic Engagement', 'Immigration', 'Economic Development',
  'Aging & Seniors', 'Early Childhood', 'Substance Abuse', 'Domestic Violence',
  'Homelessness', 'Disability Services', 'Faith-Based', 'Climate Change',
];

const GRANT_TYPES = [
  { value: 'general_operating', label: 'General Operating' },
  { value: 'program', label: 'Program / Project' },
  { value: 'capacity_building', label: 'Capacity Building' },
  { value: 'capital', label: 'Capital / Construction' },
  { value: 'seed', label: 'Seed / Startup' },
  { value: 'research', label: 'Research' },
  { value: 'equipment', label: 'Equipment / Technology' },
  { value: 'planning', label: 'Planning Grant' },
  { value: 'technical_assistance', label: 'Technical Assistance' },
];

const FUNDER_TYPES = [
  { value: 'private_foundation', label: 'Private Foundation' },
  { value: 'community_foundation', label: 'Community Foundation' },
  { value: 'corporate', label: 'Corporate / CSR' },
  { value: 'federal', label: 'Federal Government' },
  { value: 'state_gov', label: 'State Government' },
  { value: 'family_foundation', label: 'Family Foundation' },
  { value: 'donor_advised', label: 'Donor Advised Fund' },
];

const ELIGIBLE_POPULATIONS = [
  { value: 'youth', label: 'Youth / Children' },
  { value: 'seniors', label: 'Seniors / Aging' },
  { value: 'women', label: 'Women & Girls' },
  { value: 'veterans', label: 'Veterans / Military Families' },
  { value: 'bipoc', label: 'BIPOC Communities' },
  { value: 'immigrants', label: 'Immigrants / Refugees' },
  { value: 'disabilities', label: 'People with Disabilities' },
  { value: 'lgbtq', label: 'LGBTQ+' },
  { value: 'rural', label: 'Rural Communities' },
  { value: 'urban', label: 'Urban Communities' },
  { value: 'low_income', label: 'Low-Income Families' },
  { value: 'homeless', label: 'Unhoused / Homeless' },
];

const ORG_TYPES = [
  { value: '501c3', label: '501(c)(3)' },
  { value: 'community', label: 'Community-Based Org' },
  { value: 'faith_based', label: 'Faith-Based' },
  { value: 'government', label: 'Government Entity' },
  { value: 'tribal', label: 'Tribal Organization' },
  { value: 'school', label: 'School / University' },
  { value: 'healthcare', label: 'Healthcare Org' },
];

const GEO_SCOPES = [
  { value: 'national', label: 'National' },
  { value: 'state', label: 'State-Specific' },
  { value: 'regional', label: 'Regional' },
  { value: 'local', label: 'Local / County' },
  { value: 'international', label: 'International' },
];

const DEADLINE_WINDOWS = [
  { value: 'open_now', label: 'Open Now' },
  { value: 'next_30', label: 'Next 30 Days' },
  { value: 'next_90', label: 'Next 90 Days' },
  { value: 'rolling', label: 'Rolling Deadlines' },
];

const FUNDING_PRESETS = [
  { label: 'Under $10K', min: 0, max: 10000 },
  { label: '$10K - $50K', min: 10000, max: 50000 },
  { label: '$50K - $100K', min: 50000, max: 100000 },
  { label: '$100K - $500K', min: 100000, max: 500000 },
  { label: '$500K+', min: 500000, max: 5000000 },
];

function CalendarView({ grants, onGrantClick }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Build a map of deadline dates to grants
  const deadlineMap = {};
  const deadlineDates = [];
  grants.forEach((grant) => {
    if (!grant.deadline) return;
    const d = new Date(grant.deadline);
    if (isNaN(d.getTime())) return;
    const key = d.toISOString().split('T')[0];
    if (!deadlineMap[key]) deadlineMap[key] = [];
    deadlineMap[key].push(grant);
    deadlineDates.push(d);
  });

  // Grants for selected date
  const selectedKey = selectedDate.toISOString().split('T')[0];
  const selectedGrants = deadlineMap[selectedKey] || [];

  // Determine urgency for coloring
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDaysUntil = (dateStr) => {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  };

  // All grants with deadlines, sorted by date
  const upcomingGrants = grants
    .filter(g => g.deadline)
    .map(g => ({ ...g, _daysUntil: getDaysUntil(g.deadline) }))
    .sort((a, b) => a._daysUntil - b._daysUntil);

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="calendar-view">
      {/* Calendar widget */}
      <div className="lg:col-span-2">
        <Card className="bg-white">
          <CardContent className="p-4 flex justify-center">
            <CalendarWidget
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              month={selectedMonth}
              onMonthChange={setSelectedMonth}
              modifiers={{ deadline: deadlineDates }}
              modifiersStyles={{
                deadline: {
                  fontWeight: 'bold',
                  backgroundColor: 'rgb(220 252 231)',
                  borderRadius: '9999px',
                  color: 'rgb(22 101 52)',
                },
              }}
              data-testid="calendar-widget"
            />
          </CardContent>
        </Card>

        {/* Selected date detail */}
        {selectedGrants.length > 0 && (
          <Card className="bg-white mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-stone-800">
                {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {selectedGrants.map((grant) => {
                const StatusIcon = STATUS_CONFIG[grant.status]?.icon || FileText;
                return (
                  <div
                    key={grant.id}
                    className="p-3 rounded-lg border border-stone-200 hover:border-primary/30 cursor-pointer transition-colors"
                    onClick={() => onGrantClick(grant)}
                    data-testid={`calendar-grant-${grant.id}`}
                  >
                    <p className="font-medium text-sm text-stone-900">{grant.name}</p>
                    <p className="text-xs text-stone-500">{grant.funder_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-xs ${STATUS_CONFIG[grant.status]?.color}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {STATUS_CONFIG[grant.status]?.label}
                      </Badge>
                      {grant.amount_requested && (
                        <span className="text-xs text-stone-400">${grant.amount_requested.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upcoming deadlines list */}
      <div className="lg:col-span-3 space-y-3">
        <h3 className="font-semibold text-stone-800 flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          Upcoming Deadlines
        </h3>
        {upcomingGrants.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-10 text-center">
              <Calendar className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 text-sm">No grant deadlines set yet.</p>
              <p className="text-stone-400 text-xs mt-1">Add deadlines to your grants in the "My Grants" tab to see them here.</p>
            </CardContent>
          </Card>
        ) : (
          upcomingGrants.map((grant) => {
            const daysUntil = grant._daysUntil;
            const isPast = daysUntil < 0;
            const isUrgent = daysUntil >= 0 && daysUntil <= 3;
            const isSoon = daysUntil > 3 && daysUntil <= 14;
            const StatusIcon = STATUS_CONFIG[grant.status]?.icon || FileText;

            let urgencyColor = 'border-l-stone-300';
            let urgencyBadge = null;
            if (isPast) {
              urgencyColor = 'border-l-stone-300 opacity-50';
              urgencyBadge = <Badge className="bg-stone-100 text-stone-500 text-xs">Past</Badge>;
            } else if (isUrgent) {
              urgencyColor = 'border-l-red-500';
              urgencyBadge = <Badge className="bg-red-100 text-red-700 text-xs">{daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}</Badge>;
            } else if (isSoon) {
              urgencyColor = 'border-l-amber-500';
              urgencyBadge = <Badge className="bg-amber-100 text-amber-700 text-xs">{daysUntil} days</Badge>;
            } else {
              urgencyColor = 'border-l-green-500';
              urgencyBadge = <Badge className="bg-green-100 text-green-700 text-xs">{daysUntil} days</Badge>;
            }

            return (
              <Card
                key={grant.id}
                className={`bg-white border-l-4 ${urgencyColor} hover:shadow-sm transition-shadow cursor-pointer`}
                onClick={() => onGrantClick(grant)}
                data-testid={`deadline-card-${grant.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-stone-900 text-sm">{grant.name}</h4>
                      <p className="text-xs text-stone-500">{grant.funder_name}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge className={`text-xs ${STATUS_CONFIG[grant.status]?.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[grant.status]?.label}
                        </Badge>
                        {grant.amount_requested && (
                          <span className="text-xs text-stone-500 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />${grant.amount_requested.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-stone-700">
                        {new Date(grant.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                      {urgencyBadge}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function GrantsHub() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFocusAreas, setSelectedFocusAreas] = useState([]);
  const [fundingMin, setFundingMin] = useState('');
  const [fundingMax, setFundingMax] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sortBy, setSortBy] = useState('match_score'); // match_score | deadline | funding_desc | funding_asc
  const [selectedForBulk, setSelectedForBulk] = useState(new Set());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedGrantTypes, setSelectedGrantTypes] = useState([]);
  const [selectedFunderTypes, setSelectedFunderTypes] = useState([]);
  const [selectedPopulations, setSelectedPopulations] = useState([]);
  const [selectedOrgType, setSelectedOrgType] = useState('');
  const [selectedGeoScope, setSelectedGeoScope] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDeadlineWindow, setSelectedDeadlineWindow] = useState('');
  
  // Watchlist state
  const [watchlist, setWatchlist] = useState([]);
  
  // My grants state
  const [myGrants, setMyGrants] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [loiDialogOpen, setLoiDialogOpen] = useState(false);
  const [generatedLOI, setGeneratedLOI] = useState('');
  const [generatingLOI, setGeneratingLOI] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  
  // Add grant dialog
  const [addGrantOpen, setAddGrantOpen] = useState(false);
  const [editGrantOpen, setEditGrantOpen] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);
  const [savingGrant, setSavingGrant] = useState(false);
  const [grantForm, setGrantForm] = useState({
    name: '',
    funder_name: '',
    funder_website: '',
    deadline: '',
    amount_requested: '',
    status: 'planning',
    requirements: '',
    notes: '',
    application_url: '',
  });

  // Grant writing templates state
  const [grantTemplates, setGrantTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ title: '', template_type: 'loi', content: '', tags: '', notes: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');

  // AI template review state
  const [reviewingTemplate, setReviewingTemplate] = useState(false);
  const [templateReview, setTemplateReview] = useState(null);

  const isPaidPlan = user?.subscription_plan === 'basic' || user?.subscription_plan === 'pro' || user?.subscription_plan === 'trial' || user?.subscription_plan === 'grants_pro' || user?.is_lifetime_pro;
  const isFreeUser = !isPaidPlan;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWatchlist();
    fetchMyGrants();
    fetchStats();
    fetchGrantTemplates();
    fetchSavedSearches();
  }, [user, authLoading, navigate]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const fetchWatchlist = async () => {
    if (!isPaidPlan) return;
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/watchlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWatchlist(data);
      }
    } catch (error) {
      console.error('Error fetching watchlist:', error);
    }
  };

  const fetchMyGrants = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyGrants(data);
      }
    } catch (error) {
      console.error('Error fetching grants:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Grant writing templates
  const fetchGrantTemplates = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setGrantTemplates(await res.json());
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.title.trim() || !templateForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSavingTemplate(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const isEdit = !!editingTemplate;
      const url = isEdit ? `${API}/api/grants/templates/${editingTemplate.id}` : `${API}/api/grants/templates`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...templateForm,
          tags: templateForm.tags ? templateForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        toast.success(isEdit ? 'Template updated' : 'Template saved');
        setTemplateDialogOpen(false);
        setEditingTemplate(null);
        setTemplateForm({ title: '', template_type: 'loi', content: '', tags: '', notes: '' });
        if (isEdit && viewingTemplate?.id === updated.id) {
          setViewingTemplate(updated);
        }
        fetchGrantTemplates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Template deleted');
        setViewingTemplate(null);
        fetchGrantTemplates();
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      title: template.title,
      template_type: template.template_type,
      content: template.content,
      tags: (template.tags || []).join(', '),
      notes: template.notes || '',
    });
    setTemplateDialogOpen(true);
  };

  const duplicateTemplate = (template) => {
    setEditingTemplate(null);
    setTemplateForm({
      title: `${template.title} (Copy)`,
      template_type: template.template_type,
      content: template.content,
      tags: (template.tags || []).join(', '),
      notes: template.notes || '',
    });
    setTemplateDialogOpen(true);
  };

  // Saved searches
  const fetchSavedSearches = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/saved-searches`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSavedSearches(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveCurrentSearch = async () => {
    if (!saveSearchName.trim()) { toast.error('Please name your search'); return; }
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/saved-searches`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: saveSearchName,
          filters: {
            keywords: searchQuery, focus_areas: selectedFocusAreas, funding_min: fundingMin, funding_max: fundingMax,
            grant_type: selectedGrantTypes, funder_type: selectedFunderTypes, eligible_populations: selectedPopulations,
            organization_type: selectedOrgType, geographic_scope: selectedGeoScope, location: selectedLocation, deadline_window: selectedDeadlineWindow,
          }
        }),
      });
      if (res.ok) { toast.success('Search saved!'); setSaveSearchOpen(false); setSaveSearchName(''); fetchSavedSearches(); }
    } catch (e) { toast.error('Failed to save search'); }
  };

  const loadSavedSearch = (search) => {
    const f = search.filters || {};
    setSearchQuery(f.keywords || '');
    setSelectedFocusAreas(f.focus_areas || []);
    setFundingMin(f.funding_min || '');
    setFundingMax(f.funding_max || '');
    setSelectedGrantTypes(f.grant_type || []);
    setSelectedFunderTypes(f.funder_type || []);
    setSelectedPopulations(f.eligible_populations || []);
    setSelectedOrgType(f.organization_type || '');
    setSelectedGeoScope(f.geographic_scope || '');
    setSelectedLocation(f.location || '');
    setSelectedDeadlineWindow(f.deadline_window || '');
    if (f.grant_type?.length || f.funder_type?.length || f.eligible_populations?.length || f.organization_type || f.geographic_scope || f.deadline_window) {
      setShowAdvancedFilters(true);
    }
    toast.success(`Loaded "${search.name}"`);
  };

  const deleteSavedSearch = async (id) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      await fetch(`${API}/api/grants/saved-searches/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Search deleted');
      fetchSavedSearches();
    } catch (e) { toast.error('Failed to delete'); }
  };

  // AI template review
  const requestAIReview = async (templateId) => {
    setReviewingTemplate(true);
    setTemplateReview(null);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/templates/ai-review`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId }),
      });
      if (res.ok) {
        const data = await res.json();
        setTemplateReview(data.review);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'AI review failed');
      }
    } catch (e) { toast.error('Failed to get AI review'); }
    finally { setReviewingTemplate(false); }
  };

  const handleSearch = async () => {
    if (!isPaidPlan) {
      toast.error('Grant search requires Basic or Pro plan');
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keywords: searchQuery,
          focus_areas: selectedFocusAreas,
          funding_min: fundingMin ? parseInt(fundingMin) : null,
          funding_max: fundingMax ? parseInt(fundingMax) : null,
          grant_type: selectedGrantTypes,
          funder_type: selectedFunderTypes,
          eligible_populations: selectedPopulations,
          organization_type: selectedOrgType || null,
          geographic_scope: selectedGeoScope || null,
          location: selectedLocation || null,
          deadline_window: selectedDeadlineWindow || null,
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.opportunities || []);
        toast.success(`Found ${data.opportunities?.length || 0} grant opportunities`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Search failed');
      }
    } catch (error) {
      toast.error('Failed to search grants');
    } finally {
      setSearching(false);
    }
  };

  const addToWatchlist = async (opportunity) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/watchlist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_opportunity_id: opportunity.id,
          status: 'watching',
          grant_name: opportunity.name,
          funder_name: opportunity.funder_name,
          description: opportunity.description,
          focus_areas: opportunity.focus_areas,
          funding_range: opportunity.funding_range,
          deadline: opportunity.deadline,
          eligibility: opportunity.eligibility,
          requirements: opportunity.requirements,
          application_url: opportunity.application_url,
          match_score: opportunity.match_score,
        })
      });
      
      if (res.ok) {
        toast.success('Added to watchlist');
        fetchWatchlist();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add to watchlist');
      }
    } catch (error) {
      toast.error('Failed to add to watchlist');
    }
  };

  // ===== BULK SAVE + CSV EXPORT + SORT (competitive parity with Instrumentl/GS) =====
  const toggleSelectResult = (id) => {
    setSelectedForBulk(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllResults = () => {
    if (selectedForBulk.size === searchResults.length) {
      setSelectedForBulk(new Set());
    } else {
      setSelectedForBulk(new Set(searchResults.map(o => o.id)));
    }
  };

  const bulkSaveToWatchlist = async () => {
    if (selectedForBulk.size === 0) { toast.error('Select at least one grant'); return; }
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/watchlist/bulk-add`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ opportunity_ids: [...selectedForBulk] })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Saved ${data.added} grant${data.added === 1 ? '' : 's'} to watchlist${data.skipped ? ` (${data.skipped} already saved)` : ''}`);
        setSelectedForBulk(new Set());
        fetchWatchlist();
      } else {
        toast.error(data.detail || 'Bulk save failed');
      }
    } catch (e) {
      toast.error('Bulk save failed');
    }
  };

  const exportSelectedToCSV = async () => {
    const ids = selectedForBulk.size > 0 ? [...selectedForBulk] : searchResults.map(o => o.id);
    if (ids.length === 0) { toast.error('No grants to export'); return; }
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/search/export-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ opportunity_ids: ids })
      });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raiseroot-grants-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${ids.length} grant${ids.length === 1 ? '' : 's'} to CSV`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const getSortedResults = () => {
    const arr = [...searchResults];
    if (sortBy === 'match_score') {
      arr.sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
    } else if (sortBy === 'deadline') {
      arr.sort((a, b) => {
        const da = a.deadline && a.deadline !== 'Rolling' ? new Date(a.deadline).getTime() : Number.MAX_SAFE_INTEGER;
        const db = b.deadline && b.deadline !== 'Rolling' ? new Date(b.deadline).getTime() : Number.MAX_SAFE_INTEGER;
        return da - db;
      });
    } else if (sortBy === 'funding_desc') {
      arr.sort((a, b) => (b.funding_range?.max || 0) - (a.funding_range?.max || 0));
    } else if (sortBy === 'funding_asc') {
      arr.sort((a, b) => (a.funding_range?.max || 0) - (b.funding_range?.max || 0));
    }
    return arr;
  };

  const removeFromWatchlist = async (itemId) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/watchlist/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Removed from watchlist');
        fetchWatchlist();
      }
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const checkEligibility = async (opportunity) => {
    setCheckingEligibility(true);
    setEligibilityResult(null);
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/eligibility-check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_opportunity_id: opportunity.id
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setEligibilityResult(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Eligibility check failed');
      }
    } catch (error) {
      toast.error('Failed to check eligibility');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const generateLOI = async (opportunity) => {
    setGeneratingLOI(true);
    setGeneratedLOI('');
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/generate-loi`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_opportunity_id: opportunity.id
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedLOI(data.content);
        setLoiDialogOpen(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'LOI generation failed');
      }
    } catch (error) {
      toast.error('Failed to generate LOI');
    } finally {
      setGeneratingLOI(false);
    }
  };

  const createGrant = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...grantForm,
          amount_requested: grantForm.amount_requested ? parseFloat(grantForm.amount_requested) : null
        })
      });
      
      if (res.ok) {
        toast.success('Grant added to your tracker');
        setAddGrantOpen(false);
        setGrantForm({
          name: '',
          funder_name: '',
          funder_website: '',
          deadline: '',
          amount_requested: '',
          status: 'planning',
          requirements: '',
          notes: '',
          application_url: '',
        });
        fetchMyGrants();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create grant');
      }
    } catch (error) {
      toast.error('Failed to create grant');
    }
  };

  const openEditGrant = (grant) => {
    setEditingGrant({
      ...grant,
      amount_requested: grant.amount_requested || '',
    });
    setEditGrantOpen(true);
  };

  const updateGrant = async () => {
    if (!editingGrant) return;
    setSavingGrant(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/${editingGrant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingGrant.name,
          funder_name: editingGrant.funder_name,
          funder_website: editingGrant.funder_website,
          deadline: editingGrant.deadline,
          amount_requested: editingGrant.amount_requested ? parseFloat(editingGrant.amount_requested) : null,
          status: editingGrant.status,
          requirements: editingGrant.requirements,
          notes: editingGrant.notes,
        })
      });
      
      if (res.ok) {
        toast.success('Grant updated');
        setEditGrantOpen(false);
        setEditingGrant(null);
        fetchMyGrants();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update grant');
      }
    } catch (error) {
      toast.error('Failed to update grant');
    } finally {
      setSavingGrant(false);
    }
  };

  const deleteGrant = async (grantId) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/${grantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Grant deleted');
        setEditGrantOpen(false);
        setEditingGrant(null);
        fetchMyGrants();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete grant');
    }
  };

  const toggleFocusArea = (area) => {
    setSelectedFocusAreas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const getMatchScoreColor = (score) => {
    if (score >= 85) return 'text-green-600 bg-green-50';
    if (score >= 70) return 'text-blue-600 bg-blue-50';
    return 'text-stone-600 bg-[#FAFAFA]';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-20 pb-12" data-testid="grants-hub-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-primary to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]">
                Grant Hub
              </h1>
              <p className="text-stone-600">
                Search, track, and apply for grants
              </p>
            </div>
          </div>
          
          {/* Stats Summary */}
          {stats && (
            <div className="flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-primary">{stats.total}</p>
                <p className="text-xs text-stone-500">Active Grants</p>
              </div>
              <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-green-600">${(stats.total_awarded / 1000).toFixed(0)}K</p>
                <p className="text-xs text-stone-500">Awarded</p>
              </div>
              <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-amber-600">{stats.upcoming_deadlines}</p>
                <p className="text-xs text-stone-500">Upcoming</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 shadow-sm w-full sm:w-auto overflow-x-auto">
            <TabsTrigger value="search" className="gap-2 text-xs sm:text-sm">
              <Search className="w-4 h-4" />
              Find Grants
            </TabsTrigger>
            <TabsTrigger value="watchlist" className="gap-2 text-xs sm:text-sm">
              <Bookmark className="w-4 h-4" />
              Watchlist ({watchlist.length})
            </TabsTrigger>
            <TabsTrigger value="tracker" className="gap-2 text-xs sm:text-sm">
              <FileText className="w-4 h-4" />
              My Grants ({myGrants.length})
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2 text-xs sm:text-sm" data-testid="tab-calendar">
              <Calendar className="w-4 h-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2 text-xs sm:text-sm" data-testid="tab-templates">
              <FileCheck className="w-4 h-4" />
              My Templates ({grantTemplates.length})
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search" className="space-y-6">
            {isFreeUser && (
              <div className="bg-gradient-to-r from-primary/5 to-amber-50 border border-primary/20 rounded-lg p-4 flex items-center justify-between" data-testid="grants-upgrade-banner">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Lock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-stone-800">AI Grant Search requires a paid plan</p>
                    <p className="text-xs text-stone-500">Upgrade to search grants, check eligibility, and generate LOIs</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate('/pricing')} className="bg-primary hover:bg-primary-800 text-white flex-shrink-0" data-testid="grants-upgrade-btn">
                  View Plans
                </Button>
              </div>
            )}
            {/* Search Form */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Grant Search
                </CardTitle>
                <CardDescription>
                  Filter by focus area, funder type, grant type, population served, and more.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Keywords + Location row */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label>Keywords</Label>
                    <StableInput
                      placeholder="e.g., youth education, environmental conservation, food bank..."
                      value={searchQuery}
                      onChange={(f,v) => setSearchQuery(v)} field="search"
                      className="mt-1"
                      data-testid="grant-search-keywords"
                    />
                  </div>
                  <div>
                    <Label>Location / State</Label>
                    <StableInput
                      placeholder="e.g., California, Midwest, NYC"
                      value={selectedLocation}
                      onChange={(f,v) => setSelectedLocation(v)} field="location"
                      className="mt-1"
                      data-testid="grant-search-location"
                    />
                  </div>
                </div>

                {/* Funding Amount */}
                <div>
                  <Label className="mb-2 block">Funding Amount</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {FUNDING_PRESETS.map((preset) => {
                      const isActive = fundingMin === String(preset.min) && fundingMax === String(preset.max);
                      return (
                        <Badge key={preset.label} variant={isActive ? "default" : "outline"}
                          className={`cursor-pointer transition-all ${isActive ? 'bg-primary text-white' : 'hover:bg-primary-50'}`}
                          onClick={() => { setFundingMin(String(preset.min)); setFundingMax(String(preset.max)); }}
                          data-testid={`funding-preset-${preset.label.replace(/[^a-zA-Z0-9]/g, '')}`}>
                          {preset.label}
                        </Badge>
                      );
                    })}
                    {(fundingMin || fundingMax) && (
                      <Badge variant="outline" className="cursor-pointer text-red-500 hover:bg-red-50" onClick={() => { setFundingMin(''); setFundingMax(''); }}>
                        <X className="w-3 h-3 mr-1" /> Clear
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <StableInput type="number" placeholder="Min ($)" value={fundingMin} onChange={(f,v) => setFundingMin(v)} field="min" className="text-sm" />
                    <StableInput type="number" placeholder="Max ($)" value={fundingMax} onChange={(f,v) => setFundingMax(v)} field="max" className="text-sm" />
                  </div>
                </div>
                
                {/* Focus Areas */}
                <div>
                  <Label className="mb-2 block">Focus Areas</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {FOCUS_AREAS.map((area) => (
                      <Badge
                        key={area}
                        variant={selectedFocusAreas.includes(area) ? "default" : "outline"}
                        className={`cursor-pointer transition-all text-xs ${
                          selectedFocusAreas.includes(area) 
                            ? 'bg-primary text-white' 
                            : 'hover:bg-primary-50'
                        }`}
                        onClick={() => toggleFocusArea(area)}
                      >
                        {area}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Advanced Filters Toggle */}
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-800 transition-colors w-full justify-center py-2 border border-dashed border-stone-300 rounded-lg hover:border-primary/40"
                  data-testid="toggle-advanced-filters"
                >
                  <Filter className="w-4 h-4" />
                  {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                  {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {(selectedGrantTypes.length + selectedFunderTypes.length + selectedPopulations.length + (selectedOrgType ? 1 : 0) + (selectedGeoScope ? 1 : 0) + (selectedDeadlineWindow ? 1 : 0)) > 0 && (
                    <Badge className="bg-primary text-white text-xs ml-1">
                      {selectedGrantTypes.length + selectedFunderTypes.length + selectedPopulations.length + (selectedOrgType ? 1 : 0) + (selectedGeoScope ? 1 : 0) + (selectedDeadlineWindow ? 1 : 0)} active
                    </Badge>
                  )}
                </button>

                {showAdvancedFilters && (
                  <div className="space-y-5 pt-2 border-t border-stone-100">
                    {/* Grant Type */}
                    <div>
                      <Label className="mb-2 block text-stone-700">Grant Type</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {GRANT_TYPES.map((gt) => (
                          <Badge key={gt.value} variant={selectedGrantTypes.includes(gt.value) ? "default" : "outline"}
                            className={`cursor-pointer transition-all text-xs ${selectedGrantTypes.includes(gt.value) ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`}
                            onClick={() => setSelectedGrantTypes(prev => prev.includes(gt.value) ? prev.filter(v => v !== gt.value) : [...prev, gt.value])}>
                            {gt.label}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Funder Type */}
                    <div>
                      <Label className="mb-2 block text-stone-700">Funder Type</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {FUNDER_TYPES.map((ft) => (
                          <Badge key={ft.value} variant={selectedFunderTypes.includes(ft.value) ? "default" : "outline"}
                            className={`cursor-pointer transition-all text-xs ${selectedFunderTypes.includes(ft.value) ? 'bg-emerald-600 text-white' : 'hover:bg-emerald-50'}`}
                            onClick={() => setSelectedFunderTypes(prev => prev.includes(ft.value) ? prev.filter(v => v !== ft.value) : [...prev, ft.value])}>
                            {ft.label}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Eligible Populations */}
                    <div>
                      <Label className="mb-2 block text-stone-700">Eligible Populations Served</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {ELIGIBLE_POPULATIONS.map((ep) => (
                          <Badge key={ep.value} variant={selectedPopulations.includes(ep.value) ? "default" : "outline"}
                            className={`cursor-pointer transition-all text-xs ${selectedPopulations.includes(ep.value) ? 'bg-purple-600 text-white' : 'hover:bg-purple-50'}`}
                            onClick={() => setSelectedPopulations(prev => prev.includes(ep.value) ? prev.filter(v => v !== ep.value) : [...prev, ep.value])}>
                            {ep.label}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Organization Type + Geographic Scope + Deadline */}
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label>Organization Type</Label>
                        <Select value={selectedOrgType} onValueChange={setSelectedOrgType}>
                          <SelectTrigger className="mt-1" data-testid="grant-search-org-type"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {ORG_TYPES.map((ot) => <SelectItem key={ot.value} value={ot.value}>{ot.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Geographic Scope</Label>
                        <Select value={selectedGeoScope} onValueChange={setSelectedGeoScope}>
                          <SelectTrigger className="mt-1" data-testid="grant-search-geo-scope"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {GEO_SCOPES.map((gs) => <SelectItem key={gs.value} value={gs.value}>{gs.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Deadline Window</Label>
                        <Select value={selectedDeadlineWindow} onValueChange={setSelectedDeadlineWindow}>
                          <SelectTrigger className="mt-1" data-testid="grant-search-deadline"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {DEADLINE_WINDOWS.map((dw) => <SelectItem key={dw.value} value={dw.value}>{dw.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Active filters summary */}
                {(selectedFocusAreas.length > 0 || selectedGrantTypes.length > 0 || selectedFunderTypes.length > 0 || selectedPopulations.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-stone-100">
                    <span className="text-xs text-stone-500 py-1">Active:</span>
                    {selectedFocusAreas.map(a => <Badge key={a} className="bg-primary/10 text-primary text-xs cursor-pointer" onClick={() => toggleFocusArea(a)}>{a} <X className="w-3 h-3 ml-1" /></Badge>)}
                    {selectedGrantTypes.map(v => <Badge key={v} className="bg-blue-100 text-blue-700 text-xs cursor-pointer" onClick={() => setSelectedGrantTypes(prev => prev.filter(x => x !== v))}>{GRANT_TYPES.find(g => g.value === v)?.label} <X className="w-3 h-3 ml-1" /></Badge>)}
                    {selectedFunderTypes.map(v => <Badge key={v} className="bg-emerald-100 text-emerald-700 text-xs cursor-pointer" onClick={() => setSelectedFunderTypes(prev => prev.filter(x => x !== v))}>{FUNDER_TYPES.find(f => f.value === v)?.label} <X className="w-3 h-3 ml-1" /></Badge>)}
                    {selectedPopulations.map(v => <Badge key={v} className="bg-purple-100 text-purple-700 text-xs cursor-pointer" onClick={() => setSelectedPopulations(prev => prev.filter(x => x !== v))}>{ELIGIBLE_POPULATIONS.find(e => e.value === v)?.label} <X className="w-3 h-3 ml-1" /></Badge>)}
                  </div>
                )}

                <Button 
                  onClick={isFreeUser ? () => navigate('/pricing') : handleSearch} 
                  disabled={searching}
                  className={`w-full h-12 text-base ${isFreeUser ? 'bg-stone-400 hover:bg-[#FAFAFA]0' : 'bg-primary hover:bg-primary-800'}`}
                  data-testid="grants-search-btn"
                >
                  {isFreeUser ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Upgrade to Search
                    </>
                  ) : searching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search Grants
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Save Search + Saved Searches */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              {!saveSearchOpen ? (
                <Button variant="outline" size="sm" onClick={() => setSaveSearchOpen(true)} data-testid="save-search-btn">
                  <Bookmark className="w-4 h-4 mr-1" /> Save This Search
                </Button>
              ) : (
                <div className="flex gap-2 items-center">
                  <StableInput value={saveSearchName} onChange={(f, v) => setSaveSearchName(v)} field="searchName" placeholder="Name your search..." className="w-64" data-testid="save-search-name" />
                  <Button size="sm" className="bg-primary hover:bg-primary-800 text-white" onClick={saveCurrentSearch} data-testid="save-search-confirm">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSaveSearchOpen(false)}>Cancel</Button>
                </div>
              )}
              {savedSearches.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-stone-500">Saved:</span>
                  {savedSearches.map((ss) => (
                    <div key={ss.id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full">
                      <button onClick={() => loadSavedSearch(ss)} className="hover:underline" data-testid={`load-search-${ss.id}`}>{ss.name}</button>
                      <button onClick={() => deleteSavedSearch(ss.id)} className="text-primary/50 hover:text-red-500 ml-0.5" data-testid={`delete-search-${ss.id}`}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-4">
                {/* Header with sort + bulk actions */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h3 className="font-semibold text-lg text-stone-800">
                    Found {searchResults.length} Grant Opportunities
                  </h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs text-stone-500 font-medium">Sort:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-xs bg-white border border-stone-200 rounded-lg px-2 py-1.5 focus:border-primary focus:outline-none"
                      data-testid="search-sort-select"
                    >
                      <option value="match_score">Best Match</option>
                      <option value="deadline">Deadline (soonest)</option>
                      <option value="funding_desc">Funding (high → low)</option>
                      <option value="funding_asc">Funding (low → high)</option>
                    </select>
                    <Button size="sm" variant="outline" onClick={exportSelectedToCSV} className="text-xs" data-testid="export-csv-btn">
                      <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                    </Button>
                  </div>
                </div>

                {/* Bulk action bar */}
                <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-stone-700">
                    <input
                      type="checkbox"
                      checked={selectedForBulk.size === searchResults.length && searchResults.length > 0}
                      onChange={selectAllResults}
                      className="w-4 h-4 accent-primary cursor-pointer"
                      data-testid="select-all-checkbox"
                    />
                    <span>{selectedForBulk.size > 0 ? `${selectedForBulk.size} selected` : 'Select all'}</span>
                  </label>
                  <Button
                    size="sm"
                    onClick={bulkSaveToWatchlist}
                    disabled={selectedForBulk.size === 0}
                    className="bg-primary hover:bg-primary-800 text-white text-xs disabled:opacity-40"
                    data-testid="bulk-save-btn"
                  >
                    <Bookmark className="w-3.5 h-3.5 mr-1" /> Save Selected to Watchlist
                  </Button>
                </div>

                <div className="grid gap-4">
                  {getSortedResults().map((opp) => (
                    <Card key={opp.id} className={`bg-white hover:shadow-md transition-shadow ${selectedForBulk.has(opp.id) ? 'border-primary/50 ring-1 ring-primary/20' : ''}`}>
                      <CardContent className="p-6">
                        <div className="flex gap-4">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedForBulk.has(opp.id)}
                            onChange={() => toggleSelectResult(opp.id)}
                            className="w-4 h-4 accent-primary cursor-pointer mt-1.5 flex-shrink-0"
                            data-testid={`select-grant-${opp.id}`}
                          />
                          <div className="flex justify-between items-start gap-4 flex-1">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h4 className="font-semibold text-lg text-[#0A0A0A]">{opp.name}</h4>
                              <Badge className={getMatchScoreColor(opp.match_score)}>
                                {opp.match_score}% Match
                              </Badge>
                              {typeof opp.success_rate === 'number' && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  ~{opp.success_rate}% success rate
                                </Badge>
                              )}
                            </div>
                            <p className="text-stone-600 font-medium mb-2">{opp.funder_name}</p>
                            <p className="text-stone-600 text-sm mb-3">{opp.description}</p>
                            
                            <div className="flex flex-wrap gap-2 mb-3">
                              {opp.focus_areas?.map((area, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {area}
                                </Badge>
                              ))}
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-stone-500">
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                ${opp.funding_range?.min?.toLocaleString()} - ${opp.funding_range?.max?.toLocaleString()}
                              </span>
                              {opp.deadline && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {opp.deadline}
                                </span>
                              )}
                              {opp.awards_per_year && (
                                <span className="flex items-center gap-1 text-xs">
                                  <Target className="w-3.5 h-3.5" />
                                  {opp.awards_per_year} awards/yr
                                </span>
                              )}
                              {opp.average_award && (
                                <span className="text-xs">
                                  Avg: ${opp.average_award.toLocaleString()}
                                </span>
                              )}
                            </div>

                            {opp.past_recipients?.length > 0 && (
                              <div className="mt-3 text-xs text-stone-500">
                                <span className="font-medium text-stone-700">Recent recipients:</span> {opp.past_recipients.slice(0, 3).join(' · ')}
                              </div>
                            )}
                            
                            {opp.match_reasons?.length > 0 && (
                              <div className="mt-3 p-2 bg-green-50 rounded-md">
                                <p className="text-xs text-green-700">
                                  <strong>Why it&apos;s a match:</strong> {opp.match_reasons.join(', ')}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedOpportunity(opp); setDetailDialogOpen(true); }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addToWatchlist(opp)}
                            >
                              <Bookmark className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary-800 text-white"
                              onClick={() => generateLOI(opp)}
                              disabled={generatingLOI}
                            >
                              {generatingLOI ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-1" />
                                  Generate LOI
                                </>
                              )}
                            </Button>
                          </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Watchlist Tab */}
          <TabsContent value="watchlist" className="space-y-4">
            {watchlist.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <Bookmark className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <h3 className="font-medium text-stone-900 mb-2">No saved grants yet</h3>
                  <p className="text-stone-500 mb-4">Search for grants and save them to your watchlist</p>
                  <Button variant="outline" onClick={() => setActiveTab('search')}>
                    <Search className="w-4 h-4 mr-2" />
                    Search Grants
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Banner for legacy entries without details */}
                {watchlist.some(item => !item.grant_name) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="watchlist-stale-banner">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Some saved grants are missing details</p>
                        <p className="text-xs text-amber-600">These were saved before the update. Remove them and re-save from a new search to see full grant info.</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0"
                      data-testid="clear-stale-watchlist-btn"
                      onClick={async () => {
                        const staleItems = watchlist.filter(item => !item.grant_name);
                        for (const item of staleItems) {
                          await removeFromWatchlist(item.id);
                        }
                        toast.success(`Removed ${staleItems.length} stale entries`);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Clear stale ({watchlist.filter(i => !i.grant_name).length})
                    </Button>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-sm text-stone-500">{watchlist.length} saved grant{watchlist.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="grid gap-4">
                  {watchlist.map((item) => {
                    const isLegacy = !item.grant_name;
                    return (
                    <Card key={item.id} className={`bg-white hover:shadow-md transition-shadow ${isLegacy ? 'opacity-60' : ''}`} data-testid={`watchlist-card-${item.id}`}>
                      <CardContent className="p-5">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                              <h4 className="font-semibold text-base text-[#0A0A0A] truncate">
                                {item.grant_name || 'Saved Grant (details unavailable)'}
                              </h4>
                              {item.match_score && (
                                <Badge className={`${getMatchScoreColor(item.match_score)} flex-shrink-0`}>
                                  {item.match_score}% Match
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                <BookmarkCheck className="w-3 h-3 mr-1" />
                                {item.status}
                              </Badge>
                            </div>

                            {item.funder_name && (
                              <p className="text-sm font-medium text-stone-600 mb-1.5">{item.funder_name}</p>
                            )}

                            {item.description && (
                              <p className="text-sm text-stone-500 mb-3 line-clamp-2">{item.description}</p>
                            )}

                            {item.focus_areas?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {item.focus_areas.map((area, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">{area}</Badge>
                                ))}
                              </div>
                            )}

                            {/* User tags */}
                            <WatchlistTags
                              item={item}
                              onSaved={fetchWatchlist}
                            />

                            <div className="flex items-center gap-5 text-sm text-stone-500">
                              {item.funding_range && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-3.5 h-3.5" />
                                  ${item.funding_range.min?.toLocaleString()} - ${item.funding_range.max?.toLocaleString()}
                                </span>
                              )}
                              {item.deadline && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {item.deadline}
                                  {parseDeadline(item.deadline) && (
                                    <span className="inline-flex items-center gap-1 ml-1.5">
                                      <a
                                        href={googleCalendarUrl(item)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[10px] text-primary hover:underline"
                                        data-testid={`gcal-btn-${item.id}`}
                                        title="Add to Google Calendar"
                                      >
                                        + Google Cal
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => downloadIcs(item)}
                                        className="text-[10px] text-stone-400 hover:text-primary"
                                        data-testid={`ics-btn-${item.id}`}
                                        title="Download .ics for Apple/Outlook"
                                      >
                                        · .ics
                                      </button>
                                    </span>
                                  )}
                                </span>
                              )}
                              <span className="flex items-center gap-1 text-xs text-stone-400">
                                <Clock className="w-3 h-3" />
                                Saved {new Date(item.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>

                          <div className="flex md:flex-col gap-2 flex-shrink-0">
                            {!isLegacy ? (
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`watchlist-view-details-${item.id}`}
                                onClick={() => {
                                  setSelectedOpportunity({
                                    id: item.grant_opportunity_id,
                                    name: item.grant_name,
                                    funder_name: item.funder_name,
                                    description: item.description,
                                    focus_areas: item.focus_areas,
                                    funding_range: item.funding_range,
                                    deadline: item.deadline,
                                    eligibility: item.eligibility,
                                    requirements: item.requirements,
                                    application_url: item.application_url,
                                    match_score: item.match_score,
                                  });
                                  setDetailDialogOpen(true);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Info
                              </Button>
                            ) : null}

                            {item.application_url && (
                              <Button
                                size="sm"
                                className="bg-primary hover:bg-primary-800 text-white"
                                asChild
                                data-testid={`watchlist-apply-${item.id}`}
                              >
                                <a href={item.application_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-4 h-4 mr-1" />
                                  Apply Now
                                </a>
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`watchlist-remove-${item.id}`}
                              onClick={() => removeFromWatchlist(item.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* My Grants Tracker Tab */}
          <TabsContent value="tracker" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg text-stone-800">Your Grant Applications</h3>
              <Button onClick={() => setAddGrantOpen(true)} className="bg-primary hover:bg-primary-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Grant
              </Button>
            </div>
            
            {myGrants.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">No grants being tracked</h3>
                  <p className="text-stone-600 mb-6 max-w-sm mx-auto">
                    Track grants you're working on — see deadlines, status, and amounts at a glance.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Button variant="outline" onClick={() => setActiveTab('search')} data-testid="empty-grants-search-btn">
                      <Search className="w-4 h-4 mr-2" /> Find a grant
                    </Button>
                    <Button onClick={() => setAddGrantOpen(true)} className="bg-primary hover:bg-primary-800 text-white" data-testid="empty-grants-add-btn">
                      <Plus className="w-4 h-4 mr-2" /> Add manually
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myGrants.map((grant) => {
                  const StatusIcon = STATUS_CONFIG[grant.status]?.icon || FileText;
                  return (
                    <Card key={grant.id} className="bg-white hover:border-primary/30 transition-colors cursor-pointer" onClick={() => openEditGrant(grant)} data-testid={`grant-card-${grant.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-stone-900">{grant.name}</h4>
                            <p className="text-stone-600 text-sm">{grant.funder_name}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                              {grant.amount_requested && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-4 h-4" />
                                  ${grant.amount_requested.toLocaleString()}
                                </span>
                              )}
                              {grant.deadline && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(grant.deadline).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={STATUS_CONFIG[grant.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {STATUS_CONFIG[grant.status]?.label}
                            </Badge>
                            <Edit className="w-4 h-4 text-stone-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4" data-testid="calendar-tab">
            <CalendarView grants={myGrants} onGrantClick={openEditGrant} />
          </TabsContent>

          {/* My Templates Tab */}
          <TabsContent value="templates" className="space-y-4" data-testid="templates-tab">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-lg text-stone-800">Your Grant Writing Templates</h3>
                <p className="text-sm text-stone-500">Save past proposals, LOIs, budgets, and narratives to reuse and adapt for future grants.</p>
              </div>
              <Button onClick={() => { setEditingTemplate(null); setTemplateForm({ title: '', template_type: 'loi', content: '', tags: '', notes: '' }); setTemplateDialogOpen(true); }} className="bg-primary hover:bg-primary-800 text-white" data-testid="new-template-btn">
                <Plus className="w-4 h-4 mr-1" /> New Template
              </Button>
            </div>

            {grantTemplates.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <FileCheck className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <h3 className="font-medium text-stone-900 mb-2">No templates saved yet</h3>
                  <p className="text-stone-500 mb-4">Save your best grant writing — past LOIs, proposals, budgets, and narratives — to reuse and tweak for future applications.</p>
                  <Button onClick={() => { setEditingTemplate(null); setTemplateForm({ title: '', template_type: 'loi', content: '', tags: '', notes: '' }); setTemplateDialogOpen(true); }} className="bg-primary hover:bg-primary-800 text-white">
                    <Plus className="w-4 h-4 mr-1" /> Create Your First Template
                  </Button>
                </CardContent>
              </Card>
            ) : viewingTemplate ? (
              <div className="space-y-4">
                <Button variant="ghost" className="text-stone-600" onClick={() => setViewingTemplate(null)} data-testid="template-back-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Back to Templates
                </Button>
                <Card className="bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-primary/10 text-primary text-xs">{TEMPLATE_TYPE_LABELS[viewingTemplate.template_type] || viewingTemplate.template_type}</Badge>
                          {viewingTemplate.tags?.map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}
                        </div>
                        <h2 className="font-heading text-xl font-bold text-[#0A0A0A]">{viewingTemplate.title}</h2>
                        <p className="text-xs text-stone-400 mt-1">{viewingTemplate.word_count} words &middot; Updated {new Date(viewingTemplate.updated_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(viewingTemplate.content); toast.success('Content copied to clipboard'); }} data-testid="template-copy-btn">
                          Copy
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => duplicateTemplate(viewingTemplate)} data-testid="template-duplicate-btn">
                          Duplicate
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditTemplate(viewingTemplate)} data-testid="template-edit-btn">
                          <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                        <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setTemplateReview(null); requestAIReview(viewingTemplate.id); }} disabled={reviewingTemplate} data-testid="template-ai-review-btn">
                          {reviewingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                          AI Review
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => deleteTemplate(viewingTemplate.id)} data-testid="template-delete-btn">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {viewingTemplate.notes && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-amber-800"><strong>Notes:</strong> {viewingTemplate.notes}</p>
                      </div>
                    )}
                    <div className="bg-[#FAFAFA] rounded-lg p-4">
                      <pre className="whitespace-pre-wrap text-sm text-stone-700 font-sans leading-relaxed">{viewingTemplate.content}</pre>
                    </div>

                    {/* AI Review Results */}
                    {reviewingTemplate && (
                      <div className="flex items-center gap-3 p-4 bg-violet-50 rounded-lg mt-4">
                        <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                        <p className="text-sm text-violet-700">AI is reviewing your grant writing... This may take a moment.</p>
                      </div>
                    )}
                    {templateReview && (
                      <div className="mt-4 space-y-4" data-testid="ai-review-results">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-stone-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /> AI Grant Writing Review</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-violet-700">{templateReview.overall_score}/10</span>
                            <Badge className={templateReview.funder_readiness === 'ready_to_submit' ? 'bg-green-100 text-green-700' : templateReview.funder_readiness === 'needs_revision' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                              {templateReview.funder_readiness === 'ready_to_submit' ? 'Ready to Submit' : templateReview.funder_readiness === 'needs_revision' ? 'Needs Revision' : 'Needs Major Revision'}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-stone-600 bg-[#FAFAFA] p-3 rounded-lg">{templateReview.summary}</p>

                        {templateReview.strengths?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-green-700 mb-2">Strengths</h4>
                            <ul className="space-y-1">{templateReview.strengths.map((s, i) => <li key={i} className="text-sm text-stone-600 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />{s}</li>)}</ul>
                          </div>
                        )}

                        {templateReview.areas_for_improvement?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-amber-700 mb-2">Areas for Improvement</h4>
                            <div className="space-y-2">{templateReview.areas_for_improvement.map((item, i) => (
                              <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-sm font-medium text-stone-800">{item.issue}</p>
                                  <Badge className={item.priority === 'high' ? 'bg-red-100 text-red-700 text-xs' : item.priority === 'medium' ? 'bg-amber-100 text-amber-700 text-xs' : 'bg-stone-100 text-stone-600 text-xs'}>{item.priority}</Badge>
                                </div>
                                <p className="text-xs text-stone-600">{item.suggestion}</p>
                              </div>
                            ))}</div>
                          </div>
                        )}

                        {templateReview.line_edits?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-blue-700 mb-2">Suggested Edits</h4>
                            <div className="space-y-2">{templateReview.line_edits.map((edit, i) => (
                              <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                                <p className="text-xs text-red-600 line-through mb-1">"{edit.original}"</p>
                                <p className="text-xs text-green-700 font-medium mb-1">"{edit.suggested}"</p>
                                <p className="text-xs text-stone-500 italic">{edit.reason}</p>
                              </div>
                            ))}</div>
                          </div>
                        )}

                        {templateReview.missing_elements?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-stone-700 mb-2">Missing Elements</h4>
                            <ul className="space-y-1">{templateReview.missing_elements.map((m, i) => <li key={i} className="text-sm text-stone-600 flex items-start gap-2"><AlertCircle className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />{m}</li>)}</ul>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {grantTemplates.map((tmpl) => (
                  <Card key={tmpl.id} className="bg-white hover:shadow-md transition-shadow cursor-pointer group" onClick={() => setViewingTemplate(tmpl)} data-testid={`template-card-${tmpl.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-primary/10 text-primary text-xs">{TEMPLATE_TYPE_LABELS[tmpl.template_type] || tmpl.template_type}</Badge>
                        <span className="text-xs text-stone-400">{tmpl.word_count} words</span>
                      </div>
                      <h4 className="font-semibold text-stone-900 mb-1 group-hover:text-primary transition-colors line-clamp-1">{tmpl.title}</h4>
                      <p className="text-xs text-stone-500 line-clamp-3 mb-2">{tmpl.content.slice(0, 200)}...</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">{tmpl.tags?.slice(0, 2).map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}</div>
                        <span className="text-xs text-stone-400">{new Date(tmpl.updated_at).toLocaleDateString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Grant Details Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOpportunity?.name}</DialogTitle>
            <DialogDescription>{selectedOpportunity?.funder_name}</DialogDescription>
          </DialogHeader>
          
          {selectedOpportunity && (
            <div className="space-y-4">
              {selectedOpportunity.description && (
                <div>
                  <h4 className="font-medium text-stone-900 mb-2">Description</h4>
                  <p className="text-stone-600">{selectedOpportunity.description}</p>
                </div>
              )}
              
              {(selectedOpportunity.funding_range?.min || selectedOpportunity.funding_range?.max || selectedOpportunity.deadline) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(selectedOpportunity.funding_range?.min || selectedOpportunity.funding_range?.max) && (
                    <div>
                      <h4 className="font-medium text-stone-900 mb-2">Funding Range</h4>
                      <p className="text-stone-600">
                        ${selectedOpportunity.funding_range?.min?.toLocaleString()} - ${selectedOpportunity.funding_range?.max?.toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-stone-900 mb-2">Deadline</h4>
                    <p className="text-stone-600">{selectedOpportunity.deadline || 'Rolling'}</p>
                  </div>
                </div>
              )}
              
              {selectedOpportunity.eligibility?.length > 0 && (
                <div>
                  <h4 className="font-medium text-stone-900 mb-2">Eligibility Requirements</h4>
                  <ul className="list-disc list-inside text-stone-600 space-y-1">
                    {selectedOpportunity.eligibility.map((req, idx) => (
                      <li key={idx}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedOpportunity.requirements?.length > 0 && (
                <div>
                  <h4 className="font-medium text-stone-900 mb-2">Application Requirements</h4>
                  <ul className="list-disc list-inside text-stone-600 space-y-1">
                    {selectedOpportunity.requirements.map((req, idx) => (
                      <li key={idx}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {!selectedOpportunity.description && !selectedOpportunity.eligibility?.length && !selectedOpportunity.requirements?.length && (
                <p className="text-stone-500 text-sm italic">Detailed grant information is not available for this entry. Try searching for the grant again to get full details.</p>
              )}
              
              {/* Eligibility Check Section */}
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => checkEligibility(selectedOpportunity)}
                  disabled={checkingEligibility}
                  className="w-full mb-4"
                >
                  {checkingEligibility ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking Eligibility...
                    </>
                  ) : (
                    <>
                      <FileCheck className="w-4 h-4 mr-2" />
                      Check My Eligibility
                    </>
                  )}
                </Button>
                
                {eligibilityResult && (
                  <div className={`p-4 rounded-lg ${eligibilityResult.eligible ? 'bg-green-50' : 'bg-amber-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {eligibilityResult.eligible ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      )}
                      <span className={`font-medium ${eligibilityResult.eligible ? 'text-green-700' : 'text-amber-700'}`}>
                        {eligibilityResult.eligible ? 'Likely Eligible' : 'May Have Concerns'}
                        ({eligibilityResult.confidence}% confidence)
                      </span>
                    </div>
                    <p className="text-sm text-stone-600 mb-2">{eligibilityResult.summary}</p>
                    
                    {eligibilityResult.strengths?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-green-700">Strengths:</p>
                        <ul className="text-xs text-stone-600">
                          {eligibilityResult.strengths.map((s, i) => <li key={i}>• {s}</li>)}
                        </ul>
                      </div>
                    )}
                    
                    {eligibilityResult.recommendations?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-stone-700">Recommendations:</p>
                        <ul className="text-xs text-stone-600">
                          {eligibilityResult.recommendations.map((r, i) => <li key={i}>• {r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
            <Button
              className="bg-primary hover:bg-primary-800 text-white"
              onClick={() => {
                generateLOI(selectedOpportunity);
                setDetailDialogOpen(false);
              }}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate LOI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LOI Dialog */}
      <Dialog open={loiDialogOpen} onOpenChange={setLoiDialogOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generated Letter of Intent</DialogTitle>
            <DialogDescription>Review and customize your LOI before sending</DialogDescription>
          </DialogHeader>
          
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap bg-[#FAFAFA] p-4 rounded-lg text-sm">
              {generatedLOI}
            </pre>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoiDialogOpen(false)}>Close</Button>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(generatedLOI);
                toast.success('LOI copied to clipboard');
              }}
            >
              Copy to Clipboard
            </Button>
            <Button
              className="bg-primary hover:bg-primary-800 text-white"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('pitchpal_token');
                  const res = await fetch(`${API}/api/grants/download-loi`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ content: generatedLOI })
                  });
                  if (res.ok) {
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'letter-of-intent.docx';
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('LOI downloaded as Word document');
                  } else {
                    toast.error('Failed to download LOI');
                  }
                } catch (error) {
                  toast.error('Failed to download LOI');
                }
              }}
              data-testid="download-loi-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download .docx
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Grant Dialog */}
      <Dialog open={addGrantOpen} onOpenChange={setAddGrantOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Add Grant to Tracker</DialogTitle>
            <DialogDescription>Track a grant application you're working on</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Grant Name *</Label>
              <StableInput
                value={grantForm.name}
                onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="name"
                placeholder="e.g., Community Impact Grant"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Funder Name *</Label>
              <StableInput
                value={grantForm.funder_name}
                onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="funder_name"
                placeholder="e.g., Local Community Foundation"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Funder Website</Label>
                <StableInput
                  value={grantForm.funder_website}
                  onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="funder_website"
                  placeholder="https://foundation.org"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Application URL</Label>
                <StableInput
                  value={grantForm.application_url}
                  onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="application_url"
                  placeholder="https://foundation.org/apply"
                  className="mt-1"
                  data-testid="add-grant-app-url"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount Requested</Label>
                <StableInput
                  type="number"
                  value={grantForm.amount_requested}
                  onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="amount_requested"
                  placeholder="50000"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Deadline</Label>
                <StableInput
                  type="date"
                  value={grantForm.deadline}
                  onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="deadline"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Status</Label>
              <Select
                value={grantForm.status}
                onValueChange={(v) => setGrantForm({...grantForm, status: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="drafting">Drafting</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="awarded">Awarded</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes</Label>
              <StableTextarea
                value={grantForm.notes}
                onChange={(f,v) => setGrantForm(prev => ({...prev, [f]: v}))} field="notes"
                placeholder="Any additional notes..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddGrantOpen(false)}>Cancel</Button>
            <Button
              onClick={createGrant}
              disabled={!grantForm.name || !grantForm.funder_name}
              className="bg-primary hover:bg-primary-800 text-white"
            >
              Add Grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Grant Dialog */}
      <Dialog open={editGrantOpen} onOpenChange={setEditGrantOpen}>
        <DialogContent className="max-w-md bg-white max-h-[80vh] overflow-y-auto" data-testid="edit-grant-dialog">
          <DialogHeader>
            <DialogTitle>Edit Grant</DialogTitle>
            <DialogDescription>Update grant details and status</DialogDescription>
          </DialogHeader>
          
          {editingGrant && (
            <div className="space-y-4">
              <div>
                <Label>Grant Name</Label>
                <StableInput
                  value={editingGrant.name}
                  onChange={(f,v) => setEditingGrant(prev => ({...prev, [f]: v}))} field="name"
                  className="mt-1"
                  data-testid="edit-grant-name"
                />
              </div>
              
              <div>
                <Label>Funder</Label>
                <StableInput
                  value={editingGrant.funder_name}
                  onChange={(f,v) => setEditingGrant(prev => ({...prev, [f]: v}))} field="funder_name"
                  className="mt-1"
                  data-testid="edit-grant-funder"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount Requested ($)</Label>
                  <StableInput
                    type="number"
                    value={editingGrant.amount_requested}
                    onChange={(f,v) => setEditingGrant(prev => ({...prev, [f]: v}))} field="amount_requested"
                    className="mt-1"
                    data-testid="edit-grant-amount"
                  />
                </div>
                <div>
                  <Label>Deadline</Label>
                  <StableInput
                    type="date"
                    value={editingGrant.deadline || ''}
                    onChange={(f,v) => setEditingGrant(prev => ({...prev, [f]: v}))} field="deadline"
                    className="mt-1"
                    data-testid="edit-grant-deadline"
                  />
                </div>
              </div>
              
              <div>
                <Label>Status</Label>
                <Select
                  value={editingGrant.status}
                  onValueChange={(v) => setEditingGrant({...editingGrant, status: v})}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-grant-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="drafting">Drafting</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="awarded">Awarded</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Requirements</Label>
                <StableTextarea
                  value={editingGrant.requirements || ''}
                  onChange={(f,v) => setEditingGrant(prev => ({...prev, [f]: v}))} field="requirements"
                  placeholder="Grant requirements..."
                  className="mt-1"
                  rows={3}
                  data-testid="edit-grant-requirements"
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <StableTextarea
                  value={editingGrant.notes || ''}
                  onChange={(f,v) => setEditingGrant(prev => ({...prev, [f]: v}))} field="notes"
                  placeholder="Any additional notes..."
                  className="mt-1"
                  rows={3}
                  data-testid="edit-grant-notes"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => editingGrant && deleteGrant(editingGrant.id)}
              className="text-red-600 hover:bg-red-50 border-red-200"
              data-testid="delete-grant-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditGrantOpen(false)}>Cancel</Button>
              <Button
                onClick={updateGrant}
                disabled={savingGrant}
                className="bg-primary hover:bg-primary-800 text-white"
                data-testid="save-grant-btn"
              >
                {savingGrant ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Grant Writing Template'}</DialogTitle>
            <DialogDescription>Save your best grant writing to reuse and adapt for future applications.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <StableInput value={templateForm.title} onChange={(f, v) => setTemplateForm(prev => ({ ...prev, [f]: v }))} field="title" placeholder="e.g., Youth Education LOI 2025" className="mt-1" data-testid="template-title-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={templateForm.template_type} onValueChange={(v) => setTemplateForm(prev => ({ ...prev, template_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="template-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="loi">Letter of Intent</SelectItem>
                    <SelectItem value="proposal">Full Proposal</SelectItem>
                    <SelectItem value="budget">Budget Narrative</SelectItem>
                    <SelectItem value="narrative">Program Narrative</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Content *</Label>
              <StableTextarea value={templateForm.content} onChange={(f, v) => setTemplateForm(prev => ({ ...prev, [f]: v }))} field="content" placeholder="Paste or write your grant content here..." rows={12} className="mt-1 font-mono text-sm" data-testid="template-content-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Tags (comma-separated)</Label>
                <StableInput value={templateForm.tags} onChange={(f, v) => setTemplateForm(prev => ({ ...prev, [f]: v }))} field="tags" placeholder="e.g., education, youth, community" className="mt-1" data-testid="template-tags-input" />
              </div>
              <div>
                <Label>Notes</Label>
                <StableInput value={templateForm.notes} onChange={(f, v) => setTemplateForm(prev => ({ ...prev, [f]: v }))} field="notes" placeholder="Reminder notes about this template..." className="mt-1" data-testid="template-notes-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTemplateDialogOpen(false); setEditingTemplate(null); }}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={savingTemplate || !templateForm.title.trim() || !templateForm.content.trim()} className="bg-primary hover:bg-primary-800 text-white" data-testid="template-save-btn">
              {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editingTemplate ? 'Update Template' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
